"use client";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { XIcon } from "@/components/ui/x";
import { cn } from "@/lib/utils";
import type { ComponentProps, ReactNode } from "react";

export type NoticeProps = ComponentProps<typeof Alert> & {
  icon?: ReactNode;
  actions?: ReactNode;
  /** Renders a close button; the caller owns hiding the Notice on click
   * (e.g. tracking dismissed ids in state) since this component is stateless. */
  onDismiss?: () => void;
};

/**
 * A single-row inline notice — an icon, a short message, and optional
 * trailing actions (e.g. "Try again") or a dismiss button. Generic enough to
 * drop in anywhere a passive status update or recoverable interruption needs
 * to show inline: the interrupted-response banner, or tool-triggered
 * feedback like "Memory updated." / "Memory removed."
 */
export const Notice = ({ icon, actions, onDismiss, children, className, ...props }: NoticeProps) => (
  <Alert
    className={cn("flex flex-wrap items-center justify-between gap-2 py-3", className)}
    {...props}
  >
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      {icon}
      <span>{children}</span>
    </div>
    {actions || onDismiss ? (
      <div className="flex shrink-0 items-center gap-2">
        {actions}
        {onDismiss ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label="Dismiss"
            onClick={onDismiss}
          >
            <XIcon size={12} />
          </Button>
        ) : null}
      </div>
    ) : null}
  </Alert>
);
